import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import style from "./style";
import countryList from "react-select-country-list";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DropdownIcon from "../../../../../assets/AccountingModule/drop_down_icon.svg";
import EditIcon from "../../../../../assets/AccountingModule/edit.svg";
import { useState } from "react";
import color from "../../../../../constants/color";
import { ReactComponent as DownArrow } from "../../../../../assets/AccountingModule/arrow_down.svg";
import { useNavigate } from "react-router-dom";
import { getDueDateFromPaymentTerm } from "../../../../../utils/Utilities";
import { paymentTermsOptions } from "../../../../../constants/data";

const EditInvoicePage = ({
  template,
  setTemplate,
  customers,
  products,
  loading,
  productInformation,
  handleFieldChange,
  handleProductChange,
  handleAddProduct,
  handleRemoveProduct,
  handleUpdateInvoice,
  total,
  discountValue,
  taxValue,
  grandTotal,
  isFormValid,
}) => {
  if (!template) return <>Loading...</>;
  const countryOptions = countryList().getData();
  const navigate = useNavigate();

  const handleCopyBillingToShipping = () => {
    setTemplate((prev) => ({
      ...prev,
      shippingAddress: {
        ...prev.shippingAddress,
        ...prev.billingAddress,
      },
    }));
  };

  const handlePaymentTermSelection = (value) => {
    handleFieldChange("paymentTerms", value);
    const dueDate = getDueDateFromPaymentTerm(value, template.invoiceDate);
    handleFieldChange("dueDate", dueDate);
  };

  return (
    <>
      <Box px="15px" mt={2}>
        <Box sx={style.headerContainer}>
          <Typography sx={style.leaveHeading}>Edit Invoice</Typography>
        </Box>
      </Box>

      <Grid container sx={style.mainContainer}>
        <Grid item xs={12} lg={11.8} sx={style.gridbox}>
          {/* Customer */}
          <Box sx={style.inputSection}>
            <Box sx={{ ...style.inputBox, width: "100%" }}>
              <Typography sx={style.inputText}>
                Customer<span style={{ color: "red" }}>*</span>
              </Typography>
              <Autocomplete
                options={[
                  { _id: "add_new", name: "+ New Customer" },
                  ...(customers || []),
                ]}
                getOptionLabel={(option) => option.name || ""}
                value={template.customer}
                onChange={(_, newValue) => {
                  if (!newValue) {
                    // handle clear
                    setTemplate((prev) => ({
                      ...prev,
                      customer: null,
                      billingAddress: {
                        country: "",
                        street1: "",
                        street2: "",
                        city: "",
                        state: "",
                        pinCode: "",
                        phone: "",
                      },
                      shippingAddress: {
                        country: "",
                        street1: "",
                        street2: "",
                        city: "",
                        state: "",
                        pinCode: "",
                        phone: "",
                      },
                    }));
                    return;
                  }

                  if (newValue?._id === "add_new") {
                    navigate("/dashboard/addNewCustomer"); // redirect page
                    return;
                  }

                  // Populate addresses when customer is selected
                  const billingAddress = newValue.billingAddress || {
                    country: "",
                    street1: "",
                    street2: "",
                    city: "",
                    state: "",
                    pinCode: "",
                    phone: "",
                  };

                  const shippingAddress = newValue.shippingAddress || {
                    country: "",
                    street1: "",
                    street2: "",
                    city: "",
                    state: "",
                    pinCode: "",
                    phone: "",
                  };

                  setTemplate((prev) => ({
                    ...prev,
                    customer: {
                      _id: newValue._id,
                      name: newValue.name,
                    },
                    billingAddress: billingAddress,
                    shippingAddress: shippingAddress,
                  }));
                }}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={
                      option._id === "add_new" ? { color: color.secondary } : {}
                    }
                  >
                    {option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    sx={{ ...style.modalInput, ...style.inputSpace }}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                    }}
                  />
                )}
              />
            </Box>
          </Box>

          {/* Billing and Shipping Address */}
          <Grid container>
            <Grid
              item
              xs={12}
              lg={11.7}
              sx={{
                ...style.gridbox,
                py: 0,
                px: { xs: 0, sm: 0 },
                borderRadius: 0,
                mt: 1,
              }}
            >
              <Box sx={style.addressBox}>
                <Box sx={{ width: { xs: "100%", lg: "50%" } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      Billing Address
                    </Typography>
                    <img src={EditIcon} height={16} width={16} alt="edit" />
                  </Box>

                  <Box sx={style.addressInput}>
                    <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                      <Typography sx={style.inputText}>
                        Country/Region
                      </Typography>
                    </Box>
                    <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                      <FormControl
                        fullWidth
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "2.8em",
                            marginTop: { xs: "0em", md: "0.5em" },
                            borderRadius: "10px",
                          },
                        }}
                      >
                        <Select
                          value={template.billingAddress.country || ""}
                          onChange={(e) =>
                            setTemplate((prev) => ({
                              ...prev,
                              billingAddress: {
                                ...prev.billingAddress,
                                country: e.target.value,
                              },
                            }))
                          }
                          name="billingCountry"
                          sx={style.dropDownMenu}
                          displayEmpty
                        >
                          <MenuItem disabled value="">
                            Select
                          </MenuItem>
                          {countryOptions.map((country, index) => (
                            <MenuItem
                              key={index}
                              sx={style.dropDownMenu}
                              value={country.label}
                            >
                              {country.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                      <Typography sx={style.inputText}>Address</Typography>
                    </Box>
                    <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        placeholder="Street 1"
                        value={template.billingAddress.street1}
                        onChange={(e) =>
                          setTemplate((prev) => ({
                            ...prev,
                            billingAddress: {
                              ...prev.billingAddress,
                              street1: e.target.value,
                            },
                          }))
                        }
                        error={false}
                        // onFocus={handleFocus}
                        autoComplete="bstreet1"
                        sx={{ ...style.addressTextField, mb: 3 }}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                      />
                      <TextField
                        variant="outlined"
                        fullWidth
                        placeholder="Street 2"
                        value={template.billingAddress.street2}
                        onChange={(e) =>
                          setTemplate((prev) => ({
                            ...prev,
                            billingAddress: {
                              ...prev.billingAddress,
                              street2: e.target.value,
                            },
                          }))
                        }
                        error={false}
                        // onFocus={handleFocus}
                        autoComplete="bstreet2"
                        sx={style.addressTextField}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  <Box sx={style.addressInput}>
                    <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                      <Typography sx={style.inputText}>City</Typography>
                    </Box>
                    <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        placeholder="City"
                        value={template.billingAddress.city}
                        onChange={(e) =>
                          setTemplate((prev) => ({
                            ...prev,
                            billingAddress: {
                              ...prev.billingAddress,
                              city: e.target.value,
                            },
                          }))
                        }
                        error={false}
                        // onFocus={handleFocus}
                        autoComplete="bcity"
                        sx={style.addressTextField}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  <Box sx={style.addressInput}>
                    <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                      <Typography sx={style.inputText}>State</Typography>
                    </Box>
                    <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        placeholder="State"
                        value={template.billingAddress.state}
                        onChange={(e) =>
                          setTemplate((prev) => ({
                            ...prev,
                            billingAddress: {
                              ...prev.billingAddress,
                              state: e.target.value,
                            },
                          }))
                        }
                        error={false}
                        // onFocus={handleFocus}
                        autoComplete="bstate"
                        sx={style.addressTextField}
                      />
                    </Box>
                  </Box>
                  <Box sx={style.addressInput}>
                    <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                      <Typography sx={style.inputText}>Pin Code</Typography>
                    </Box>
                    <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        placeholder="Pin Code"
                        value={template.billingAddress.pinCode}
                        onChange={(e) =>
                          setTemplate((prev) => ({
                            ...prev,
                            billingAddress: {
                              ...prev.billingAddress,
                              pinCode: e.target.value,
                            },
                          }))
                        }
                        error={false}
                        // onFocus={handleFocus}
                        autoComplete="bpinCode"
                        sx={style.addressTextField}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ ...style.addressInput, mb: 0 }}>
                    <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                      <Typography sx={style.inputText}>Phone</Typography>
                    </Box>
                    <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        placeholder="Phone"
                        value={template.billingAddress.phone}
                        onChange={(e) =>
                          setTemplate((prev) => ({
                            ...prev,
                            billingAddress: {
                              ...prev.billingAddress,
                              phone: e.target.value,
                            },
                          }))
                        }
                        error={false}
                        // onFocus={handleFocus}
                        autoComplete="bphone"
                        sx={style.addressTextField}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ width: { xs: "100%", lg: "50%" } }}>
                  <Box sx={{ display: "flex", gap: "8px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontWeight: "bold" }}>
                        Shipping Address
                      </Typography>
                      <img src={EditIcon} height={16} width={16} alt="edit" />
                    </Box>
                    <Typography
                      sx={{ color: color.secondary, cursor: "pointer" }}
                      onClick={handleCopyBillingToShipping}
                    >
                      {"( "}
                      <DownArrow /> Copy Billing Address{" )"}
                    </Typography>
                  </Box>
                  <Box sx={style.addressInput}>
                    <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                      <Typography sx={style.inputText}>
                        Country/Region
                        {/* <span style={{ color: "red" }}>*</span> */}
                      </Typography>
                    </Box>
                    <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                      <FormControl
                        fullWidth
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "2.8em",
                            marginTop: "0.5em",
                            borderRadius: "10px",
                          },
                        }}
                      >
                        <Select
                          value={template.shippingAddress.country || ""}
                          onChange={(e) =>
                            setTemplate((prev) => ({
                              ...prev,
                              shippingAddress: {
                                ...prev.shippingAddress,
                                country: e.target.value,
                              },
                            }))
                          }
                          name="shippingCountry"
                          sx={style.dropDownMenu}
                          // onFocus={handleFocus}
                          displayEmpty
                        // IconComponent={CustomDropdownIcon}
                        >
                          <MenuItem disabled value="">
                            Select
                          </MenuItem>
                          {countryOptions.map((country, index) => (
                            <MenuItem
                              key={index}
                              sx={style.dropDownMenu}
                              value={country.label}
                            >
                              {country.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                      <Typography sx={style.inputText}>Address</Typography>
                    </Box>
                    <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        placeholder="Street 1"
                        value={template.shippingAddress.street1}
                        onChange={(e) =>
                          setTemplate((prev) => ({
                            ...prev,
                            shippingAddress: {
                              ...prev.shippingAddress,
                              street1: e.target.value,
                            },
                          }))
                        }
                        error={false}
                        // onFocus={handleFocus}
                        autoComplete="sstreet1"
                        sx={{ ...style.addressTextField, mb: 3 }}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                      />
                      <TextField
                        variant="outlined"
                        fullWidth
                        placeholder="Street 2"
                        value={template.shippingAddress.street2}
                        onChange={(e) =>
                          setTemplate((prev) => ({
                            ...prev,
                            shippingAddress: {
                              ...prev.shippingAddress,
                              street2: e.target.value,
                            },
                          }))
                        }
                        error={false}
                        // onFocus={handleFocus}
                        autoComplete="sstreet2"
                        sx={style.addressTextField}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={style.addressInput}>
                    <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                      <Typography sx={style.inputText}>City</Typography>
                    </Box>
                    <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        placeholder="City"
                        value={template.shippingAddress.city}
                        onChange={(e) =>
                          setTemplate((prev) => ({
                            ...prev,
                            shippingAddress: {
                              ...prev.shippingAddress,
                              city: e.target.value,
                            },
                          }))
                        }
                        error={false}
                        // onFocus={handleFocus}
                        autoComplete="scity"
                        sx={style.addressTextField}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={style.addressInput}>
                    <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                      <Typography sx={style.inputText}>State</Typography>
                    </Box>
                    <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        placeholder="State"
                        value={template.shippingAddress.state}
                        onChange={(e) =>
                          setTemplate((prev) => ({
                            ...prev,
                            shippingAddress: {
                              ...prev.shippingAddress,
                              state: e.target.value,
                            },
                          }))
                        }
                        error={false}
                        // onFocus={handleFocus}
                        autoComplete="sstate"
                        sx={style.addressTextField}
                      />
                    </Box>
                  </Box>
                  <Box sx={style.addressInput}>
                    <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                      <Typography sx={style.inputText}>Pin Code</Typography>
                    </Box>
                    <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        placeholder="Pin Code"
                        value={template.shippingAddress.pinCode}
                        onChange={(e) =>
                          setTemplate((prev) => ({
                            ...prev,
                            shippingAddress: {
                              ...prev.shippingAddress,
                              pinCode: e.target.value,
                            },
                          }))
                        }
                        error={false}
                        // onFocus={handleFocus}
                        autoComplete="spinCode"
                        sx={style.addressTextField}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ ...style.addressInput, mb: 0 }}>
                    <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                      <Typography sx={style.inputText}>Phone</Typography>
                    </Box>
                    <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        placeholder="Phone"
                        value={template.shippingAddress.phone}
                        onChange={(e) =>
                          setTemplate((prev) => ({
                            ...prev,
                            shippingAddress: {
                              ...prev.shippingAddress,
                              phone: e.target.value,
                            },
                          }))
                        }
                        error={false}
                        // onFocus={handleFocus}
                        autoComplete="sphone"
                        sx={style.addressTextField}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Invoice Date, Payment Terms, and Due Date */}
          <Box sx={style.inputSection}>
            {/* Invoice Date */}
            <Box sx={style.inputBox}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <InputLabel sx={style.inputText}>
                  Invoice Date<span style={{ color: "red" }}>*</span>
                </InputLabel>
                <DatePicker
                  value={dayjs(template.invoiceDate)}
                  onChange={(date) =>
                    handleFieldChange("startOn", date?.toISOString())
                  }
                  sx={style.dateInput}
                  slotProps={{
                    textField: { fullWidth: true },
                    field: {
                      readOnly: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>

            {/* Payment Terms */}
            <Box sx={style.inputBox}>
              <FormControl
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "2.5em",
                    borderRadius: "10px",
                  },
                }}
              >
                <Typography sx={style.inputText}>
                  Payment Terms<span style={{ color: "red" }}>*</span>
                </Typography>
                <Select
                  value={template.paymentTerms}
                  onChange={(e) => {
                    handlePaymentTermSelection(e.target.value);
                  }}
                  fullWidth
                  displayEmpty
                  renderValue={(selected) =>
                    selected ? (
                      selected
                    ) : (
                      <span style={{ color: "#aaa" }}>
                        Select Payment Terms
                      </span>
                    )
                  }
                >
                  <MenuItem disabled value="">
                    <span style={{ color: "#999" }}>Select Payment Terms</span>
                  </MenuItem>
                  {paymentTermsOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Due Date */}
            <Box sx={style.inputBox}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <InputLabel sx={style.inputText}>
                  Due Date<span style={{ color: "red" }}>*</span>
                </InputLabel>
                <DatePicker
                  // label="End On"
                  value={dayjs(template.dueDate)}
                  minDate={dayjs(template.invoiceDate)}
                  onChange={(date) =>
                    handleFieldChange("endOn", date?.toISOString())
                  }
                  sx={style.dateInput}
                  slotProps={{
                    textField: { fullWidth: true },
                    field: {
                      readOnly: true,
                    },
                  }}
                  disabled={template.paymentTerms !== "Custom"}
                />
              </LocalizationProvider>
            </Box>
          </Box>

          {/* Daily Penalty Amount and Other Information */}
          <Box sx={style.inputSection}>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={4}>
                <Box sx={{ ...style.inputBox, width: "100%" }}>
                  <Typography sx={style.inputText}>
                    Daily Penalty Amount<span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    type="number"
                    value={template.dailyPenaltyAmount}
                    onChange={(e) =>
                      handleFieldChange("dailyPenaltyAmount", e.target.value)
                    }
                    fullWidth
                    sx={{
                      ...style.addressTextField,
                      borderRadius: "10px",
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} lg={8}>
                <Box sx={{ ...style.inputBox, width: "100%" }}>
                  <Typography sx={style.inputText}>
                    Other Information
                  </Typography>
                  <TextField
                    value={template.otherInformation}
                    onChange={(e) =>
                      handleFieldChange("otherInformation", e.target.value)
                    }
                    fullWidth
                    placeholder="(Optional)"
                    sx={{
                      ...style.addressTextField,
                      borderRadius: "10px",
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Product Information */}
        <Box sx={style.itemSection}>
          <Box
            sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2 }}
          ></Box>
          <Typography sx={style.gridText}>
            Product Informations<span style={{ color: "red" }}>*</span>
          </Typography>
        </Box>

        <Grid item xs={12} lg={11.8} sx={style.gridbox}>
          {productInformation.map((product, index) => {
            const stockQuantity =
              products.find((p) => p._id === product.productId)?.quantity || 0;
            return (
              <Box sx={style.leaveSection} key={index}>
                <Grid container sx={style.itemGrid}>
                  <Grid item xs={12} lg={2}>
                    <Autocomplete
                      options={[
                        { _id: "add_new", title: "+ New Product" },
                        ...(products || []),
                      ]}
                      getOptionLabel={(option) => option.title || ""}
                      value={
                        products.find((p) => p._id === product.productId) ||
                        null
                      }
                      onChange={(_, newVal) => {
                        if (newVal?._id === "add_new") {
                          navigate("/dashboard/addNewItem"); // redirect page
                          return;
                        }
                        if (!newVal) {
                          handleProductChange("", index, "name");
                          handleProductChange("", index, "invoiceQuantity");
                          return;
                        }
                        handleProductChange(
                          index,
                          "productId",
                          newVal?._id || ""
                        );
                      }}
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          style={
                            option._id === "add_new"
                              ? { color: color.secondary }
                              : {}
                          }
                        >
                          {option.title}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Product Name"
                          sx={{ ...style.modalInput, ...style.inputSpace }}
                          variant="outlined"
                          InputProps={{
                            ...params.InputProps,
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      placeholder="Description"
                      value={product.description}
                      onChange={(e) =>
                        handleProductChange(
                          index,
                          "description",
                          e.target.value
                        )
                      }
                      fullWidth
                      InputProps={{
                        sx: {
                          borderRadius: "8px",
                          my: 1,
                          fontSize: "14px",
                          fontWeight: "400",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={1.9}>
                    <TextField
                      placeholder="Unit Cost"
                      type="number"
                      value={product.unitCost}
                      onChange={(e) =>
                        handleProductChange(index, "unitCost", e.target.value)
                      }
                      fullWidth
                      InputProps={{
                        inputProps: {
                          min: 0,
                        },
                        sx: {
                          borderRadius: "8px",
                          my: 1,
                          fontSize: "14px",
                          fontWeight: "400",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} lg={1.9}>
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{ ...style.customInput, width: "100%" }}
                    >
                      <TextField
                        variant="outlined"
                        value={product?.quantity}
                        type="number"
                        placeholder="Quantity"
                        sx={{
                          width: "100%",
                          paddingRight: "0px !important",
                          "& .MuiOutlinedInput-root": {
                            paddingRight: "0px !important",
                          },
                        }}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          const maxQuantity = stockQuantity;
                          if (e.target.value === "") {
                            handleProductChange(index, "quantity", "");
                            return;
                          }

                          if (isNaN(value)) return;

                          const clampedValue = Math.min(
                            Math.max(0, value),
                            maxQuantity
                          );
                          handleProductChange(index, "quantity", clampedValue);
                        }}
                        InputProps={{
                          inputProps: {
                            min: 0,
                            max: stockQuantity, // Set maximum value
                          },
                          endAdornment: (
                            <InputAdornment position="end" variant="filled">
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  bgcolor: "#DEDEDE",
                                  height: 43,
                                  p: 0,
                                  minWidth: 50, // Ensure minimum width
                                  maxWidth: 120, // Prevent excessive width
                                  ...style.inputBgBox,
                                }}
                              >
                                <Box
                                  sx={{
                                    minWidth: 21,
                                    maxWidth: 80, // Limit width to prevent overflow
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: "600",
                                    userSelect: "none",
                                    borderRadius: "8px",
                                    fontSize: "14px", // Smaller font for long numbers
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    px: 0.5,
                                  }}
                                >
                                  {stockQuantity}
                                </Box>
                                <Tooltip
                                  arrow
                                  placement="top"
                                  title={
                                    <Box
                                      sx={{
                                        bgcolor: "white",
                                        p: 1,
                                        borderRadius: "6px",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "10px",
                                          fontWeight: 600,
                                          color: "#373D3F",
                                          mb: 1,
                                        }}
                                      >
                                        Stock Details
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: "10px",
                                          color: "#373D3F",
                                          fontWeight: 400,
                                        }}
                                      >
                                        Stock on Hand : {stockQuantity || "-"}
                                      </Typography>
                                      {product?.quantity &&
                                        Number(product?.quantity) >= 1000 && (
                                          <Typography
                                            sx={{
                                              fontSize: "10px",
                                              color: "#373D3F",
                                              fontWeight: 400,
                                              mt: 0.5,
                                            }}
                                          >
                                            Exact Quantity :{" "}
                                            {Number(
                                              product?.quantity
                                            ).toLocaleString()}
                                          </Typography>
                                        )}
                                    </Box>
                                  }
                                  componentsProps={{
                                    tooltip: {
                                      sx: {
                                        bgcolor: "white !important",
                                        p: 0,
                                        border: "1px solid #878787 !important",
                                        boxShadow: "none !important",
                                        "& .MuiTooltip-arrow": {
                                          color: "white !important",
                                          "&::before": {
                                            border: "1px solid #878787",
                                            backgroundColor: "white !important",
                                          },
                                        },
                                      },
                                    },
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    edge="end"
                                    sx={{
                                      color: "#878787",
                                      padding: "4px",
                                    }}
                                  >
                                    <InfoOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={1.5}>
                    <TextField
                      type="number"
                      placeholder="Amount"
                      value={product.amount}
                      disabled
                      fullWidth
                      InputProps={{
                        sx: {
                          borderRadius: "8px",
                          my: 1,
                          fontSize: "14px",
                          fontWeight: "400",
                        },
                      }}
                    />
                  </Grid>
                  {index === 0 && (
                    <Grid item xs={1.5} lg={0.5}>
                      <Box
                        sx={{
                          ...style.addIcon,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "100%",
                          height: "100%",
                        }}
                        onClick={handleAddProduct}
                      >
                        +
                      </Box>
                    </Grid>
                  )}
                  {index !== 0 && (
                    <Grid item xs={1.5} lg={0.5}>
                      <Box
                        sx={{
                          ...style.deleteIcon,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "100%",
                          height: "100%",
                        }}
                        onClick={() => handleRemoveProduct(index)}
                      >
                        <DeleteOutlineIcon />
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            );
          })}
        </Grid>

        {/* Discount and Tax */}
        <Box sx={style.itemSection}>
          <Box
            sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2 }}
          ></Box>
          <Typography sx={style.gridText}>Discount and Tax(%)</Typography>
        </Box>

        <Grid container sx={style.lastGrid}>
          {/* Discount and Tax */}
          <Grid item xs={12} lg={7.6} sx={style.gridboxs}>
            <Grid container>
              <Box sx={{ ...style.inputSection, width: "100%", gap: 3.5 }}>
                <Box sx={{ ...style.inputBox, width: "50%" }}>
                  <Typography sx={style.inputText}>
                    Discount<span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    type="number"
                    value={template.discountPercentage}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      let value = Number(inputValue);
                      // If not a number, ignore
                      if (isNaN(value)) return;
                      // Clamp between 0 and 100
                      if (value < 0) return;
                      if (value > 100) return;
                      handleFieldChange("discountPercentage", e.target.value);
                    }}
                    fullWidth
                    InputProps={{
                      inputProps: {
                        min: 0,
                        max: 100,
                      },
                      sx: {
                        borderRadius: "8px",
                        my: 1,
                        fontSize: "16px",
                        fontWeight: "400",
                      },
                    }}
                  />
                </Box>

                <Box sx={{ ...style.inputBox, width: "50%" }}>
                  <Typography sx={style.inputText}>Tax</Typography>
                  <TextField
                    type="number"
                    value={template.taxPercentage}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      let value = Number(inputValue);
                      // If not a number, ignore
                      if (isNaN(value)) return;
                      // Clamp between 0 and 100
                      if (value < 0) return;
                      if (value > 100) return;
                      handleFieldChange("taxPercentage", e.target.value);
                    }}
                    fullWidth
                    InputProps={{
                      inputProps: {
                        min: 0,
                        max: 100,
                      },
                      sx: {
                        borderRadius: "8px",
                        my: 1,
                        fontSize: "16px",
                        fontWeight: "400",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Total */}
          <Grid
            item
            xs={12}
            lg={4.2}
            sx={{ ...style.gridboxs, marginTop: "-17px !important" }}
          >
            <Box sx={style.leaveSection}>
              <Box sx={style.inputContainerBox}>
                <Box sx={style.key}>
                  <Typography sx={style.descValueText}>Total</Typography>
                </Box>
                <Box sx={style.key}>
                  <Typography sx={style.descValueText}>{total}</Typography>
                </Box>
              </Box>
              <Box sx={style.inputContainerBox}>
                <Box sx={style.key}>
                  <Typography sx={style.descValueText}>Discount</Typography>
                </Box>
                <Box sx={style.key}>
                  <Typography sx={style.descValueText}>
                    -{discountValue} ({template.discountPercentage}%)
                  </Typography>
                </Box>
              </Box>
              <Box sx={style.inputContainerBox}>
                <Box sx={style.key}>
                  <Typography sx={style.descValueText}>Tax</Typography>
                </Box>
                <Box sx={style.key}>
                  <Typography sx={style.descValueText}>
                    +{taxValue} ({template.taxPercentage}%)
                  </Typography>
                </Box>
              </Box>
              <Box sx={style.inputContainerBox}>
                <Box sx={style.key}>
                  <Typography sx={style.descValueTexts}>Grand Total</Typography>
                </Box>
                <Box sx={style.key}>
                  <Typography sx={style.descValueTexts}>
                    {grandTotal}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Save Changes Button */}
        <Box sx={style.mainBoxes}>
          <Box sx={style.createJob}>
            <Button
              variant="contained"
              sx={style.cancelButtons}
              disabled={!isFormValid()}
              onClick={handleUpdateInvoice}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </Grid>
    </>
  );
};

export default EditInvoicePage;
